import React, { useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useGlobalFontSize } from '../../../context/GlobalFontSizeContext';

const TYPE_PREFIX = {
  volume: 'Volume',
  weather: 'Weather',
  rain: 'Weather',
  temperature: 'Weather',
  market: 'Market',
  price: 'Price',
};

const formatWeekDateRange = (weekStart, weekEnd, locale = 'en-US') => {
  if (!weekStart || !weekEnd) return null;
  const start = new Date(weekStart);
  const end = new Date(weekEnd);
  const opts = { month: 'short', day: 'numeric' };
  return `${start.toLocaleDateString(locale, opts)} - ${end.toLocaleDateString(locale, opts)}`;
};

const getImpactColor = (marketImpact) => {
  const level = (marketImpact || '').toLowerCase();
  if (level === 'high' || level === 'alto') {
    return { dot: 'bg-red-500', text: 'text-red-500' };
  }
  if (level === 'medium' || level === 'medio') {
    return { dot: 'bg-orange-400', text: 'text-orange-500' };
  }
  if (level === 'low' || level === 'bajo') {
    return { dot: 'bg-bloom-green', text: 'text-bloom-green' };
  }
  return { dot: 'bg-[#828382]', text: 'text-[#828382]' };
};

const capitalize = (str) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const shortImpact = (text) => {
  const value = String(text || '').trim();
  if (!value) return '';
  const first = value.split(/(?<=\.)\s/)[0] || value;
  return first.length > 90 ? `${first.slice(0, 87).trim()}…` : first;
};

const withTypePrefix = (title, prefix) => {
  const value = String(title || '').trim();
  if (!value) return prefix;
  if (value.toLowerCase().startsWith(`${prefix.toLowerCase()}:`)) return value;
  return `${prefix}: ${value}`;
};

const TriggeredAlertCard = ({ alert, onDismiss }) => {
  const { fontSize } = useGlobalFontSize();
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'es' ? 'es-ES' : 'en-US';
  const [isExpanded, setIsExpanded] = useState(false);

  const typeKey = String(alert.alert_type || '').toLowerCase();
  const title = withTypePrefix(alert.title || '', TYPE_PREFIX[typeKey] || 'Alert');
  const weekNumber = alert.week || '';
  const weekDateRange = formatWeekDateRange(alert.week_start, alert.week_end, locale);
  const marketImpact = alert.market_impact || '';
  const commodity = alert.commodity || '';
  const isSmart = (alert.tab_type || '').toLowerCase() === 'default' || alert.tab_type === 'smart';
  const impactColors = getImpactColor(marketImpact);
  const impactLabel = capitalize(marketImpact);

  const weekPart = weekNumber && weekDateRange
    ? `Week ${weekNumber}: ${weekDateRange}`
    : weekDateRange || (weekNumber ? `Week ${weekNumber}` : '');
  const collapsedDetail = [shortImpact(alert.impact_analysis), weekPart]
    .filter(Boolean)
    .join(' · ');
  const fullAnalysis = String(alert.impact_analysis || '').trim();
  const hasExpandedContent =
    fullAnalysis.length > shortImpact(fullAnalysis).length ||
    Boolean(alert.range_change) ||
    Boolean(alert.varieties_affected) ||
    Boolean(alert.time_period);

  return (
    <div className="relative w-full rounded-[10px] border border-[#D5D8D6] dark:border-[#5d625f] bg-white dark:bg-[#1d1d1d] px-4 py-3">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDismiss?.(alert);
        }}
        className="absolute right-3 top-3 text-[#828382] hover:text-[#282A29] dark:hover:text-white transition-colors"
        aria-label="Dismiss"
      >
        <X size={16} />
      </button>

      <div className="min-w-0 pr-7">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <span
              className="font-['Inter'] font-semibold text-[#282A29] dark:text-white"
              style={{ fontSize: fontSize + 1 }}
            >
              {capitalize(commodity)}
            </span>
            <span
              className={`inline-flex items-center rounded-[4px] px-1.5 py-0.5 font-['Inter'] font-semibold uppercase tracking-wide ${
                isSmart ? 'bg-[#E3F5EB] text-[#12823F]' : 'bg-[#E8F0FE] text-[#2563EB]'
              }`}
              style={{ fontSize: fontSize - 4 }}
            >
              {isSmart ? 'Smart' : 'Custom'}
            </span>
            {impactLabel ? (
              <span className="inline-flex items-center gap-1.5">
                <span className={`h-1.5 w-1.5 rounded-full ${impactColors.dot}`} />
                <span
                  className={`font-['Inter'] font-medium ${impactColors.text}`}
                  style={{ fontSize: fontSize - 2 }}
                >
                  {impactLabel}
                </span>
              </span>
            ) : null}
          </div>

          <p
            className="mt-1 font-['Inter'] font-semibold text-[#282A29] dark:text-white"
            style={{ fontSize }}
          >
            {title}
          </p>

          {collapsedDetail ? (
            <p
              className="mt-1 font-['Inter'] text-[#5D625F] dark:text-[#C3C4C3]"
              style={{ fontSize: fontSize - 2 }}
            >
              {collapsedDetail}
            </p>
          ) : null}

          {hasExpandedContent && (
            <button
              type="button"
              onClick={() => setIsExpanded((v) => !v)}
              className="mt-2 inline-flex items-center gap-1 font-['Inter'] font-medium text-bloom-green hover:text-bloom-green-hover transition-colors"
              style={{ fontSize: fontSize - 2 }}
              aria-expanded={isExpanded}
            >
              {isExpanded ? t('alerts.triggered.showLessCard') : t('alerts.triggered.showMoreCard')}
              <ChevronDown
                size={14}
                className={`transition-transform duration-300 ease-out ${isExpanded ? 'rotate-180' : ''}`}
              />
            </button>
          )}

          {hasExpandedContent && (
            <div
              className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
              }`}
            >
              <div className="overflow-hidden min-h-0">
                <div
                  className={`mt-2 flex flex-col gap-2 border-t border-[#E8EBE9] dark:border-[#5d625f] pt-2 transition-opacity duration-300 ease-out ${
                    isExpanded ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  {fullAnalysis ? (
                    <p className="text-[#5D625F] dark:text-[#C3C4C3]" style={{ fontSize: fontSize - 2 }}>
                      {fullAnalysis}
                    </p>
                  ) : null}
                  {alert.range_change ? (
                    <p className="text-[#5D625F] dark:text-[#C3C4C3]" style={{ fontSize: fontSize - 2 }}>
                      {alert.range_change}
                    </p>
                  ) : null}
                  {alert.varieties_affected ? (
                    <p className="text-[#5D625F] dark:text-[#C3C4C3]" style={{ fontSize: fontSize - 2 }}>
                      {t('alerts.triggered.varietiesAffected')}: {alert.varieties_affected}
                    </p>
                  ) : null}
                  {alert.time_period ? (
                    <p className="text-[#5D625F] dark:text-[#C3C4C3]" style={{ fontSize: fontSize - 2 }}>
                      {t('alerts.triggered.timePeriod')}: {alert.time_period}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default TriggeredAlertCard;
